import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { createQueryOptions } from '../../../packages/Query';
import { mergeDeep } from '../../utils';

type searchQueryObject = {
  search?: {
    [key: string]: {
      [key: string]: string;
    };
  };
  order?: {
    [key: string]: string;
  };
};
type SearchParamKeys<T extends readonly string[]> = {
  [K in T[number]]: string;
};

/** Used to Create a search object from the search params when interacting with givebacks API's*/
function useCreateSearchObject() {
  const [searchParams] = useSearchParams();
  const search = useMemo(() => {
    let finalSearch = {};
    const currentParams = Object.fromEntries([...searchParams]);
    const tempObject = {};
    Object.entries(currentParams).forEach((entry) => {
      const [param, value] = entry;
      const tempParamArray = param.replaceAll(']', '').split('[');
      const right = tempParamArray.reduceRight((obj, next, index) => {
        return { [next]: index == tempParamArray.length - 1 ? value : obj };
      }, {});

      const combined = mergeDeep(tempObject, right);
      finalSearch = combined;
    });
    return finalSearch;
  }, [searchParams]) as searchQueryObject;

  return { searchObject: search };
}

const useUrlSearchParams = <T extends string[]>(
  keys: [...T],
  fallback = ''
): SearchParamKeys<T> => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    return keys.reduce((acc, key) => {
      acc[key as keyof SearchParamKeys<T>] = searchParams.get(key) ?? fallback;
      return acc;
    }, {} as SearchParamKeys<T>);
  }, [searchParams, keys, fallback]);
};

/**
 * Given a search object, this hook will update the search params in the URL
 * The output is specific to givebacks API's
 * */
const useSearchObjectToParams = <T,>({
  searchObject,
  withPagination = true,
}: {
  searchObject: T;
  withPagination?: boolean;
}) => {
  const [, setSearchParams] = useSearchParams();
  const { queryString } = createQueryOptions<any>({ search: searchObject, withPagination });

  const updateSearchParams = useCallback(() => {
    setSearchParams(queryString);
  }, [queryString, setSearchParams]);

  return { updateSearchParams };
};

export { useCreateSearchObject, useSearchObjectToParams, useUrlSearchParams };
